import { useCallback, useEffect, useMemo, useState } from 'react'

import { equals } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { RefreshOutlined } from '@mui/icons-material'
import { Box, IconButton, Tooltip } from '@mui/material'

import { ErrorComponent, Loading, LoadingOverlay } from '@common/components'
import { parseApiErrors, useSmallScreen } from '@common/utils'

import FloorsSelector from './components/FloorsSelector'
import LayerSelector, { LAYER_STATUS } from './components/LayerSelector'
import MapExportButton from './components/MapExportButton'
import MapLegend from './components/MapLegend'
import UnitsMap, { UNIT_CELL_WIDTH } from './components/UnitsMap'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} props.property
 * @param {string} props.property.id
 */
export default function ConnectivityMap({ property }) {
  const isSmallScreen = useSmallScreen()
  const {
    connectivityMapDataError,
    connectivityMapDataIsLoading,
    doFetchConnectivityMapData,
    doResetConnectivityMapState,
    doShowSnackbar,
  } = useConnect(
    'selectConnectivityMapDataError',
    'selectConnectivityMapDataIsLoading',
    'doFetchConnectivityMapData',
    'doResetConnectivityMapState',
    'doShowSnackbar',
  )

  const [data, setData] = useState(null)
  const [availableFloors, setAvailableFloors] = useState(null)
  const [selectedLayer, setSelectedLayer] = useState(LAYER_STATUS)
  const [selectedFloors, setSelectedFloors] = useState([])
  const [pdfExporting, setPdfExporting] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const fetchData = useCallback(
    async ({ refresh } = {}) => {
      try {
        if (refresh) setRefreshing(true)
        const result = await doFetchConnectivityMapData({ property })
        if (result?.table) {
          const floors = Object.values(result.table.columns).filter(Number).map(Number)
          const updatedAvailableFloors = {
            min: Math.min(...floors),
            max: Math.max(...floors),
          }
          if (!refresh || !equals(availableFloors, updatedAvailableFloors)) {
            setSelectedFloors(
              updatedAvailableFloors.max - updatedAvailableFloors.min > 10
                ? [updatedAvailableFloors.min, updatedAvailableFloors.min + 9]
                : [updatedAvailableFloors.min, updatedAvailableFloors.max],
            )
          }
          setData(result)
          setAvailableFloors(updatedAvailableFloors)

          return result
        }
        return null
      } catch (err) {
        const parsedError = parseApiErrors(err?.response)
        doShowSnackbar(parsedError, 'error')
        return null
      } finally {
        if (refresh) setRefreshing(false)
      }
    },
    [property, availableFloors],
  )

  useEffect(() => {
    fetchData()
    return () => {
      doResetConnectivityMapState()
    }
  }, [property])

  const floorsRange = useMemo(() => {
    const [start, end] = selectedFloors
    return Array.from({ length: end - start + 1 }, (_, index) => start + index)
  }, [selectedFloors])

  const columns = useMemo(
    () =>
      data?.table?.columns?.filter(
        (floor) => !floor || floorsRange.includes(Number(floor)),
      ) ?? [],
    [data, floorsRange],
  )

  const rows = useMemo(() => {
    if (data?.table?.rows) {
      return data.table.rows
        .map((row) => {
          const filteredObj = {}
          floorsRange.forEach((floor) => {
            if (floor in row) {
              filteredObj[floor] = row[floor]
            }
          })
          filteredObj.null = row.null
          return filteredObj
        })
        .filter((row) => Object.values(row).some((value) => value !== undefined))
    }
    return []
  }, [data, floorsRange])

  const pdfOptions = {
    onChangePDFExportStatus: (status) => setPdfExporting(status === 'started'),
    width: UNIT_CELL_WIDTH * columns.length + 1,
    height: 45 * rows.length + 140,
  }

  const RefreshIcon = useCallback(
    () => (
      <RefreshOutlined
        sx={{
          animation:
            connectivityMapDataIsLoading && refreshing
              ? 'spin 1s linear infinite'
              : undefined,
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      />
    ),
    [connectivityMapDataIsLoading, refreshing],
  )

  const actionsDisabled = connectivityMapDataIsLoading || !data || pdfExporting

  return (
    <Box mt={2}>
      {isSmallScreen.mobile ? (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" flexDirection="column" gap={2} width={1}>
            <LayerSelector
              value={selectedLayer}
              onChange={setSelectedLayer}
              disabled={actionsDisabled}
            />
            <FloorsSelector
              value={selectedFloors}
              onSave={setSelectedFloors}
              availableFloors={availableFloors}
              disabled={actionsDisabled}
            />
          </Box>
          <Box display="flex" gap={2}>
            <MapExportButton
              fullWidth
              disabled={actionsDisabled}
              data={data?.raw}
              layer={selectedLayer}
              property={property}
              pdfOptions={pdfOptions}
            />
            <Tooltip title="Refresh">
              <div>
                <IconButton
                  color="primary"
                  size="small"
                  disabled={actionsDisabled}
                  onClick={() => fetchData({ refresh: true })}
                >
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <Box display="flex" justifyContent="space-between" alignItems="end">
          <Box display="flex" alignItems="end" gap={2} width={1}>
            <LayerSelector
              value={selectedLayer}
              onChange={setSelectedLayer}
              disabled={actionsDisabled}
            />
            <FloorsSelector
              value={selectedFloors}
              onSave={setSelectedFloors}
              availableFloors={availableFloors}
              disabled={actionsDisabled}
            />
            <Tooltip title="Refresh">
              <div>
                <IconButton
                  color="primary"
                  size="small"
                  disabled={actionsDisabled}
                  onClick={() => fetchData({ refresh: true })}
                >
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Box>
          <MapExportButton
            disabled={actionsDisabled}
            data={data?.raw}
            layer={selectedLayer}
            property={property}
            pdfOptions={pdfOptions}
          />
        </Box>
      )}
      {data?.table?.legend && (
        <MapLegend data={data} layer={selectedLayer} sx={{ mt: 1 }} />
      )}
      {data && (
        <LoadingOverlay loading={connectivityMapDataIsLoading}>
          <UnitsMap
            property={property}
            data={data}
            layer={selectedLayer}
            columns={columns}
            rows={rows}
            pdfExporting={pdfExporting}
            onProvisionerModeEnabled={async (setPopoverData) => {
              const result = await fetchData({ refresh: true })
              if (result) {
                setPopoverData((prev) => {
                  const unitId = prev?.unit?.id
                  const updatedUnitData = result.raw?.find((unit) => unit.id === unitId)
                  return { ...prev, unit: updatedUnitData ?? prev.unit }
                })
              }
            }}
          />
        </LoadingOverlay>
      )}
      {!data && connectivityMapDataIsLoading && !connectivityMapDataError && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={1}
          mt={10}
        >
          <Loading height="100%" size={60} />
        </Box>
      )}
      {!data && !connectivityMapDataIsLoading && connectivityMapDataError && (
        <ErrorComponent
          title=""
          buttonTitle="Refresh"
          errorMessage="Oops. Something went wrong"
          callback={fetchData}
        />
      )}
    </Box>
  )
}
